import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, MapDecoder, StringDecoder, field } from '@common/encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { AccessRight } from '@stamhoofd/types/AccessRight';
import { AccessRightHelper } from './AccessRight.js';
import { PermissionLevel } from '@stamhoofd/types/PermissionLevel';
import { getPermissionLevelNumber } from './PermissionLevel.js';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
import { downgradeResourceKeys, isPeriodScopedResourceType, PermissionsResourceKey, upgradeResourceKeys } from './PermissionsResourceType.js';
import { ResourcePermissions } from './ResourcePermissions.js';
export class PermissionRole extends AutoEncoder {
    id;
    name = '';
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PermissionRole.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PermissionRole.prototype, "name", void 0);
export class PermissionRoleDetailed extends PermissionRole {
    /**
     * Generic access to all resources
     */
    level = PermissionLevel.None;
    accessRights = [];
    resources = new Map();
    /**
     * @deprecated
     * Required for migration
     */
    legacyManagePayments = false;
    /**
     * @deprecated
     * Required for migration
     */
    legacyFinanceDirector = false;
    /**
     * @deprecated
     * Required for migration
     */
    legacyCreateWebshops = false;
    compress() {
        if (this.level === PermissionLevel.Full) {
            this.accessRights = [];
            this.resources = new Map();
            return;
        }
        // we don't delete resources in the current period, because the current period might change over time
        for (const [type, resources] of this.resources) {
            const coverage = ResourcePermissions.baseline(this);
            const all = resources.get(PermissionsResourceKey.All);
            if (all?.isCoveredBy(coverage)) {
                resources.delete(PermissionsResourceKey.All);
            }
            else if (all) {
                coverage.add(all);
            }
            for (const [id, resource] of resources) {
                if (id === PermissionsResourceKey.All) {
                    continue;
                }
                if (resource.isEmpty || (id !== PermissionsResourceKey.CurrentPeriod && resource.isCoveredBy(coverage))) {
                    resources.delete(id);
                }
            }
            if (resources.size === 0) {
                this.resources.delete(type);
            }
        }
    }
    getDiffValue() {
        return this.getDescription();
    }
    getDescription() {
        const stack = [];
        if (this.level === PermissionLevel.Read) {
            stack.push($t(`%m7`));
        }
        if (this.level === PermissionLevel.Write) {
            stack.push($t(`%m8`));
        }
        if (this.level === PermissionLevel.Full) {
            stack.push($t(`%m9`));
            return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
        }
        for (const right of this.accessRights) {
            stack.push(AccessRightHelper.getDescription(right));
        }
        stack.push(...ResourcePermissions.getMapDescription(this.resources));
        if (stack.length === 0) {
            return $t(`%8v`);
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
    }
    hasAccess(level) {
        return getPermissionLevelNumber(this.level) >= getPermissionLevelNumber(level);
    }
    hasAccessRight(right) {
        const gl = AccessRightHelper.autoGrantRightForLevel(right);
        if ((gl && this.hasAccess(gl)) || this.accessRights.includes(right)) {
            return true;
        }
        const autoInherit = AccessRightHelper.autoInheritFrom(right);
        for (const r of autoInherit) {
            if (this.hasAccessRight(r)) {
                return true;
            }
        }
        return false;
    }
    getResourcePermissions(type, id) {
        const resource = this.resources.get(type);
        if (!resource) {
            return null;
        }
        const keys = [id];
        if (id !== PermissionsResourceKey.All) {
            keys.push(PermissionsResourceKey.All);
            if (isPeriodScopedResourceType(type) && id !== PermissionsResourceKey.CurrentPeriod) {
                keys.push(PermissionsResourceKey.CurrentPeriod);
            }
        }
        const instances = keys.map(key => resource.get(key)).filter(instance => instance !== undefined);
        if (instances.length === 0) {
            return null;
        }
        return instances.reduce((merged, instance) => merged.merge(instance));
    }
    getMergedResourcePermissions(type, id) {
        let base = this.getResourcePermissions(type, id);
        if (getPermissionLevelNumber(this.level) > getPermissionLevelNumber(base?.level ?? PermissionLevel.None)) {
            if (!base) {
                base = ResourcePermissions.create({ level: this.level });
            }
            base.level = this.level;
        }
        return base;
    }
    hasResourceAccess(type, id, level) {
        if (this.hasAccess(level)) {
            return true;
        }
        return this.getResourcePermissions(type, id)?.hasAccess(level) ?? false;
    }
    hasResourceAccessRight(type, id, right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        return this.getResourcePermissions(type, id)?.hasAccessRight(right) ?? false;
    }
    hasAccessRightForSomeResourceOfType(type, right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        const resource = this.resources.get(type);
        if (!resource) {
            return false;
        }
        for (const r of resource.values()) {
            if (r.hasAccessRight(right)) {
                return true;
            }
        }
        return false;
    }
    add(other) {
        if (getPermissionLevelNumber(this.level) < getPermissionLevelNumber(other.level)) {
            this.level = other.level;
        }
        for (const right of other.accessRights) {
            if (!this.accessRights.includes(right)) {
                this.accessRights.push(right);
            }
        }
        for (const [type, r] of other.resources) {
            for (const [id, resource] of r) {
                if (!this.resources.has(type)) {
                    this.resources.set(type, new Map());
                }
                const current = this.resources.get(type).get(id);
                if (!current) {
                    this.resources.get(type).set(id, resource);
                }
                else {
                    this.resources.get(type).set(id, current.merge(resource));
                }
            }
        }
    }
    get isEmpty() {
        let resourcesEmpty = true;
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                if (!r.isEmpty) {
                    resourcesEmpty = false;
                    break;
                }
            }
        }
        return this.level === PermissionLevel.None && this.accessRights.length === 0 && resourcesEmpty;
    }
    removeAccessRights(rights) {
        this.accessRights = this.accessRights.filter(r => !rights.includes(r));
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                r.removeAccessRights(rights);
            }
        }
    }
}
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel), version: 201 })
], PermissionRoleDetailed.prototype, "level", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(new EnumDecoder(AccessRight)),
        version: 246,
        upgrade: function () {
            const base = [];
            if (this.legacyManagePayments) {
                base.push(AccessRight.OrganizationManagePayments);
            }
            if (this.legacyFinanceDirector) {
                base.push(AccessRight.OrganizationFinanceDirector);
            }
            if (this.legacyCreateWebshops) {
                base.push(AccessRight.OrganizationCreateWebshops);
            }
            return base;
        },
    })
], PermissionRoleDetailed.prototype, "accessRights", void 0);
__decorate([
    field({
        decoder: new MapDecoder(new EnumDecoder(PermissionsResourceType), new MapDecoder(
        // ID
        StringDecoder, ResourcePermissions)),
        version: 248,
    }),
    field({
        decoder: new MapDecoder(new EnumDecoder(PermissionsResourceType), new MapDecoder(
        // ID
        StringDecoder, ResourcePermissions)),
        version: 418,
        upgrade: upgradeResourceKeys,
        downgrade: downgradeResourceKeys,
        upgradePatch: upgradeResourceKeys,
        downgradePatch: downgradeResourceKeys,
    })
], PermissionRoleDetailed.prototype, "resources", void 0);
__decorate([
    field({ decoder: BooleanDecoder, field: 'managePayments', optional: true })
], PermissionRoleDetailed.prototype, "legacyManagePayments", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 199, field: 'financeDirector', optional: true })
], PermissionRoleDetailed.prototype, "legacyFinanceDirector", void 0);
__decorate([
    field({ decoder: BooleanDecoder, field: 'createWebshops', optional: true })
], PermissionRoleDetailed.prototype, "legacyCreateWebshops", void 0);
export class PermissionRoleForResponsibility extends PermissionRoleDetailed {
    responsibilityId;
    responsibilityGroupId = null;
}
__decorate([
    field({ decoder: StringDecoder })
], PermissionRoleForResponsibility.prototype, "responsibilityId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PermissionRoleForResponsibility.prototype, "responsibilityGroupId", void 0);
//# sourceMappingURL=PermissionRole.js.map